import * as React from 'react';

import { IntentResult, IntentType } from '@40seas/entities/intents';
import { Resources, resourcePropertyProvider } from '@40seas/frontend/dashboard/providers';
import { datadogRumProvider } from '@40seas/frontend/libs/providers';
import { IntentCallbacks, ModalAnalyticsEvent, PaymentContextType } from '@40seas/payments-shared';
import { Box, Modal } from '@mui/material';
import { useDashboardEventTracker } from 'frontend/apps/dashboard/src/hooks/useDashboardEventTracker/useDashboardEventTracker';
import jwt_decode from 'jwt-decode';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import DefaultDataProvider from '../../../../providers/defaultDataProvider';
import { IntentsEmbed } from './IntentsEmbed';
import { IntentData, IntentParams, ModalStore, defaultModalStore } from './types';

const IntentsModalContext = React.createContext<ModalStore>(defaultModalStore);

export const useIntentsModal = (): ModalStore => {
    return React.useContext(IntentsModalContext);
};

export const initIntent = async ({
    dataProvider,
    intentType,
    intentPayload,
    tenant,
    existingToken,
    obtainNewToken,
    initPayload,
}: {
    dataProvider: DefaultDataProvider;
    intentType: IntentType;
    intentPayload?: object;
    tenant?: string;
    existingToken?: string;
    obtainNewToken?: boolean;
    initPayload?: object;
}): Promise<IntentData> => {
    const tenantHeaders = tenant ? { '40seas-tenant': tenant } : undefined;

    const token = await getToken({
        dataProvider,
        intentType,
        intentPayload,
        tenantHeaders,
        existingToken,
        obtainNewToken,
    });
    const { intentType: targetIntentType } = jwt_decode(token) as { intentType: IntentType };

    const { data: intent } = await dataProvider.postResourceProperty<PaymentContextType>(
        Resources.Intents,
        resourcePropertyProvider.intents.init(targetIntentType),
        initPayload,
        { headers: { ...tenantHeaders, '40seas-token': token } },
    );

    return { tenant, token, intent };
};

export const getToken = async ({
    dataProvider,
    intentType,
    intentPayload,
    tenantHeaders,
    existingToken,
    obtainNewToken,
}: {
    dataProvider: DefaultDataProvider;
    intentType: IntentType;
    intentPayload?: object;
    tenantHeaders?: {
        '40seas-tenant': string;
    };
    existingToken?: string;
    obtainNewToken?: boolean;
}): Promise<string> => {
    if (!existingToken || obtainNewToken) {
        const intentResponse = await dataProvider.postResourceProperty<IntentResult>(
            Resources.Intents,
            resourcePropertyProvider.intents.create(intentType),
            intentPayload,
            { headers: { ...tenantHeaders, ...(existingToken ? { '40seas-token': existingToken } : {}) } },
        );
        return intentResponse?.data?.token;
    }
    return existingToken;
};

export const IntentsModal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [params, setModalData] = React.useState<IntentParams | null>(null);
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider() as DefaultDataProvider;

    const tracker = useDashboardEventTracker();
    const trackOpenDialog = tracker.useTrackClick(ModalAnalyticsEvent.OPEN_DIALOG);

    const modalStore: ModalStore = {
        openModal: ({ intentType, intentPayload, modalData, callbacks, tenant, isEmbedded, token, user, configurationOverrides }) => {
            let cancelled = false;

            trackOpenDialog(intentType);

            setModalData({
                intentType,
                isLoading: true,
                callbacks: {
                    onClose: () => {
                        cancelled = true;
                        setModalData(null);
                    },
                },
                isEmbedded,
            });

            const wrappedCallbacks: IntentCallbacks = {
                ...callbacks,
                onConfirm: (data?: any) => {
                    setModalData(null);
                    callbacks?.onConfirm?.(data);
                    refresh();
                },
                onClose: (data?: any) => {
                    setModalData(null);
                    callbacks?.onClose?.(data);
                    refresh();
                },
            };

            const intentPromise = modalData
                ? Promise.resolve({ intent: modalData })
                : initIntent({ dataProvider, intentType, intentPayload, tenant, existingToken: token });

            intentPromise
                .then((data) => {
                    if (cancelled) return;
                    if (user) {
                        data.intent.authentication = { ...data.intent.authentication, user };
                    }
                    setModalData({
                        intentType,
                        isLoading: false,
                        callbacks: wrappedCallbacks,
                        data,
                        isEmbedded,
                        configurationOverrides,
                    });
                })
                .catch((error) => {
                    notify(datadogRumProvider.getErrorMessage(error), { type: 'error' });
                });
        },
    };

    return (
        <>
            <IntentsModalContext.Provider value={modalStore}>{children}</IntentsModalContext.Provider>
            <Modal open={!!params} sx={{ display: 'grid', justifyContent: 'center', alignContent: 'center' }}>
                <>
                    {!!params && (
                        <Box height="100dvh" display="grid">
                            <IntentsEmbed params={params} />
                        </Box>
                    )}
                </>
            </Modal>
        </>
    );
};
